import { useId } from 'react';

/**
 * O símbolo do ObraPlan — a mesma figura de `public/favicon.svg` e dos ícones de
 * instalação, desenhada em linha para o menu e a tela de entrada.
 *
 * Três barras de cronograma em cascata: o que já foi feito é sólido e termina na mesma
 * linha vertical (a linha de hoje); o que falta é translúcido. É o produto num quadrado:
 * um plano, e quanto dele já virou obra.
 *
 * As cores são da marca, não do tema: a telha é azul e as barras são brancas nos dois
 * modos, como um logotipo — a mesma escolha do painel escuro da tela de entrada. Por isso
 * o gradiente é fixo aqui, e não lê `--color-brand`, que no escuro vira azul-claro sobre
 * fundo escuro.
 *
 * `useId` porque o gradiente precisa de um `id`, e a marca aparece duas vezes na mesma
 * página (barra lateral e gaveta do celular): dois `<linearGradient>` com o mesmo nome
 * funcionam, mas é HTML inválido e o leitor de tela não precisa adivinhar.
 */
export function BrandMark({ className = 'size-8' }: { className?: string }) {
  const id = useId();
  const gradiente = `marca-fundo-${id}`;
  return (
    <svg
      viewBox="0 0 64 64"
      aria-hidden="true"
      className={`shrink-0 ${className}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={gradiente} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#1c7ccb" />
          <stop offset="1" stopColor="#0a4a84" />
        </linearGradient>
      </defs>
      <rect width="64" height="64" rx="14" fill={`url(#${gradiente})`} />
      <g fill="#fff">
        <rect x="12" y="13" width="24" height="10" rx="5" />
        <rect x="18" y="27" width="34" height="10" rx="5" opacity=".4" />
        <path d="M23 27H36V37H23A5 5 0 0 1 18 32A5 5 0 0 1 23 27Z" />
        <rect x="24" y="41" width="28" height="10" rx="5" opacity=".4" />
        <path d="M29 41H36V51H29A5 5 0 0 1 24 46A5 5 0 0 1 29 41Z" />
      </g>
    </svg>
  );
}

/**
 * O nome, como logotipo: "Obra" em semibold e "Plan" em bold, na IBM Plex Sans que o
 * sistema já carrega. Texto, e não imagem, para herdar a cor do lugar onde está — escuro
 * no menu claro, claro no menu escuro — e para o leitor de tela ler "ObraPlan".
 */
export function BrandWordmark({ className = 'text-[15px]' }: { className?: string }) {
  return (
    <span className={`font-semibold leading-none tracking-[-0.015em] ${className}`}>
      Obra<span className="font-bold">Plan</span>
    </span>
  );
}
